/* Evira cart enhancements. Shopify owns all final prices and discounts. */
(() => {
  'use strict';

  class EviraCartEnhancements {
    constructor() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      if (!this.drawer) return;
      this.root = window.Shopify?.routes?.root || '/';
      this.handle = this.drawer.dataset.eviraProtectionHandle || 'shipping-protection';
      this.variantId = Number(this.drawer.dataset.eviraProtectionVariant || 0);
      this.protectionPrice = Number(this.drawer.dataset.eviraProtectionPrice || 0);
      this.offerReady = this.drawer.dataset.eviraOfferReady === 'true';
      this.discountName = (this.drawer.dataset.eviraDiscountName || 'Mix & Match').toLowerCase();
      this.currency = this.drawer.dataset.cartCurrency || 'GBP';
      this.requestNumber = 0;
      this.products = new Map();
      this.drawer.addEventListener('click', (event) => this.onClick(event));
      this.drawer.addEventListener('change', (event) => this.onChange(event));
      window.addEventListener('evira:cart:render', (event) => this.update(event.detail.cart));
      // The server-rendered drawer may already contain items before its first Ajax update.
      fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('Cart unavailable')))
        .then((cart) => this.update(cart))
        .catch(() => {});
    }

    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: this.currency
      }).format((Number(cents) || 0) / 100);
    }

    escape(value) {
      return String(value ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
    }

    isProtection(item) {
      const urlHandle = String(item.url || '').split('/products/')[1]?.split(/[/?#]/)[0];
      return item.handle === this.handle || item.product_handle === this.handle ||
        urlHandle === this.handle || (this.variantId && Number(item.variant_id) === this.variantId);
    }

    tier(count) {
      return count >= 5 ? 25 : count === 4 ? 20 : count === 3 ? 15 : count === 2 ? 10 : 0;
    }

    update(cart) {
      if (!cart?.items) return;
      this.cart = cart;
      const regular = cart.items.filter((item) => !this.isProtection(item));
      const orphan = cart.items.find((item) => this.isProtection(item));
      if (!regular.length && orphan && !this.removingOrphan) {
        this.removingOrphan = true;
        fetch(`${this.root}cart/change.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: orphan.key, quantity: 0 })
        }).then((response) => {
          if (!response.ok) throw new Error('Unable to remove orphaned Shipping Protection');
          return window.eviraCartDrawer?.refresh();
        }).catch((error) => this.showError(error.message)).finally(() => { this.removingOrphan = false; });
      }
      this.updateOffer(cart);
      this.updateProtection(cart);
      this.updateRecommendations(cart);
    }

    updateOffer(cart) {
      const offer = this.drawer.querySelector('[data-evira-offer]');
      if (!offer || !this.offerReady) return;
      const eligible = cart.items.filter((item) => !this.isProtection(item));
      const count = eligible.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const tier = this.tier(count);
      const next = count >= 5 ? 0 : count < 2 ? 2 : count + 1;
      const nextTier = this.tier(next);
      const status = offer.querySelector('[data-evira-offer-status]');
      const message = offer.querySelector('[data-evira-offer-message]');
      const fill = offer.querySelector('[data-evira-offer-fill]');
      const badge = this.drawer.querySelector('[data-evira-offer-badge]');
      const matching = (application) => Boolean(application) && (
        String(application.title || '').toLowerCase().includes(this.discountName) ||
        (tier > 0 && application.value_type === 'percentage' && Number(application.value) === tier)
      );
      let discount = 0;
      let protectionDiscounted = false;
      const appliedRates = new Set();
      cart.items.forEach((item) => (item.line_level_discount_allocations || item.discount_allocations || []).forEach((allocation) => {
        const application = allocation.discount_application || cart.discount_applications?.[allocation.discount_application_index];
        if (!matching(application)) return;
        if (this.isProtection(item)) protectionDiscounted = true;
        else {
          discount += Number(allocation.amount || 0);
          if (application?.value_type === 'percentage') appliedRates.add(Number(application.value));
        }
      }));
      if (!discount && !protectionDiscounted && !cart.items.some((item) => this.isProtection(item))) {
        (cart.cart_level_discount_applications || []).filter(matching).forEach((application) => {
          discount += Number(application.total_allocated_amount || 0);
          if (application.value_type === 'percentage') appliedRates.add(Number(application.value));
        });
      }

      status.textContent = discount && !protectionDiscounted ? `${this.money(discount)} saved` :
        tier ? `${tier}% tier` : 'SAVE UP TO 25%';
      if (protectionDiscounted) {
        message.textContent = 'Shipping Protection is excluded from the item count. Your cart total shows any applied discounts.';
      } else if (count >= 5) {
        message.textContent = discount ? 'Maximum tier reached. Your cart savings are applied.' :
          '5+ eligible items · highest offer tier reached.';
      } else {
        message.textContent = count === 0 ? 'Add 2 fashion items to reach the first tier.' :
          `Add ${next - count} more eligible item${next - count === 1 ? '' : 's'} to reach ${nextTier}% off.`;
        if (tier) message.textContent = `${count} eligible items · ${tier}% tier. ${message.textContent}`;
      }
      fill.style.width = `${Math.min(100, Math.max(0, (count - 1) * 25))}%`;
      offer.querySelectorAll('[data-tier]').forEach((node) => {
        node.classList.toggle('is-active', count >= Number(node.dataset.tier));
      });
      if (badge) {
        const title = badge.querySelector('[data-evira-badge-title]');
        const detail = badge.querySelector('[data-evira-badge-detail]');
        const saving = badge.querySelector('[data-evira-badge-saving]');
        const appliedRate = appliedRates.size === 1 ? [...appliedRates][0] : null;
        badge.hidden = !tier;
        badge.classList.toggle('is-applied', Boolean(discount && !protectionDiscounted));
        badge.classList.toggle('is-warning', protectionDiscounted);
        badge.querySelector('.evira-offer-badge__icon').textContent = protectionDiscounted ? '!' : discount ? '✓' : '%';
        saving.hidden = !discount || protectionDiscounted;
        saving.textContent = discount && !protectionDiscounted ? `−${this.money(discount)}` : '';
        if (protectionDiscounted) {
          title.textContent = 'OFFER NEEDS A QUICK FIX';
          detail.textContent = 'Shipping Protection is receiving the Mix & Match discount.';
        } else if (discount) {
          title.textContent = appliedRate ? `${appliedRate}% OFF APPLIED` : 'MIX & MATCH APPLIED';
          detail.textContent = 'Your Mix & Match savings are in your bag';
        } else if (tier) {
          title.textContent = `${tier}% OFF UNLOCKED`;
          detail.textContent = 'Your Mix & Match offer is ready';
        }
      }
    }

    updateProtection(cart) {
      const card = this.drawer.querySelector('[data-evira-protection]');
      const toggle = card?.querySelector('[data-evira-protection-toggle]');
      if (!toggle) return;
      const item = cart.items.find((line) => this.isProtection(line));
      const hasMerchandise = cart.items.some((line) => !this.isProtection(line));
      card.classList.toggle('is-selected', Boolean(item));
      toggle.setAttribute('aria-checked', item ? 'true' : 'false');
      toggle.setAttribute('aria-label', item ? 'Remove Shipping Protection' : `Add Shipping Protection for ${this.money(this.protectionPrice)}`);
      card.querySelector('[data-evira-protection-description]').textContent = item || this.variantId
        ? `Covered if your order is lost, stolen or damaged · ${this.money(this.protectionPrice)}`
        : 'Set up the product to enable';
      toggle.disabled = card.dataset.unavailable === 'true' || this.busyProtection === true || (!hasMerchandise && !item);
    }

    async onClick(event) {
      const protection = event.target.closest('[data-evira-protection-toggle]');
      if (protection && !protection.disabled && !this.busyProtection) {
        await this.toggleProtection();
        return;
      }
      const add = event.target.closest('[data-evira-upsell-add]');
      if (add && !add.disabled) await this.addRecommendation(add);
    }

    onChange(event) {
      const select = event.target.closest('[data-evira-upsell-option]');
      if (!select) return;
      const card = select.closest('[data-evira-upsell-product]');
      const product = this.products.get(Number(card?.dataset.eviraUpsellProduct));
      if (!card || !product) return;
      const choices = [...card.querySelectorAll('[data-evira-upsell-option]')].map((node) => node.value);
      const variant = product.variants.find((item) => item.available &&
        item.options.every((value, index) => value === choices[index]));
      const button = card.querySelector('[data-evira-upsell-add]');
      const price = card.querySelector('[data-evira-upsell-price]');
      button.dataset.variantId = variant?.id || '';
      button.disabled = !variant;
      button.textContent = variant ? 'Add to bag +' : 'Combination unavailable';
      if (variant) price.textContent = this.money(variant.price);
    }

    async toggleProtection() {
      if (!this.variantId || !this.cart) return;
      this.busyProtection = true;
      this.updateProtection(this.cart);
      const existing = this.cart.items.find((line) => this.isProtection(line));
      const endpoint = existing ? 'cart/change.js' : 'cart/add.js';
      const payload = existing ? { id: existing.key, quantity: 0 } : { id: this.variantId, quantity: 1 };
      try {
        const response = await fetch(`${this.root}${endpoint}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error((await response.json().catch(() => null))?.description || 'Unable to update protection');
        await window.eviraCartDrawer?.refresh();
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.busyProtection = false;
        if (this.cart) this.updateProtection(this.cart);
      }
    }

    async addRecommendation(button) {
      const variantId = Number(button.dataset.variantId || 0);
      if (!variantId) return;
      button.disabled = true;
      button.textContent = 'Adding…';
      try {
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        if (!response.ok) throw new Error((await response.json().catch(() => null))?.description || 'Unable to add item');
        await window.eviraCartDrawer?.refresh();
      } catch (error) {
        this.showError(error.message);
        button.disabled = false;
        button.textContent = 'Add to bag +';
      }
    }

    showError(message) {
      const region = this.drawer.querySelector('[data-cart-drawer-live-region]');
      if (region) region.textContent = message || 'Please try again.';
    }

    async updateRecommendations(cart) {
      const container = this.drawer.querySelector('[data-evira-upsell]');
      const request = ++this.requestNumber;
      if (!container) return;
      const eligible = cart.items.filter((item) => !this.isProtection(item));
      if (!eligible.length) { container.hidden = true; return; }
      const ids = new Set(cart.items.map((item) => Number(item.product_id)));
      try {
        const url = `${this.root}recommendations/products.json?product_id=${encodeURIComponent(eligible[0].product_id)}&limit=10&intent=related`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Recommendations unavailable');
        const data = await response.json();
        if (request !== this.requestNumber || !container.isConnected) return;
        const products = (data.products || []).filter((product) =>
          !ids.has(Number(product.id)) && product.handle !== this.handle &&
          product.available && product.variants?.some((variant) => variant.available)).slice(0, 3);
        this.products = new Map(products.map((product) => [Number(product.id), product]));
        if (!products.length) { container.hidden = true; return; }
        container.innerHTML = `<div class="evira-upsell__heading"><span>COMPLETE YOUR LOOK</span><small>Curated for your bag</small></div>${products.map((product) => this.card(product)).join('')}`;
        container.hidden = false;
      } catch (_) {
        if (request === this.requestNumber) container.hidden = true;
      }
    }

    card(product) {
      const available = product.variants.filter((variant) => variant.available);
      const first = available[0];
      const options = (product.options || []).map((option, index) => ({
        name: typeof option === 'string' ? option : option?.name, index
      })).filter(({ name }) => name && name !== 'Title').map(({ name, index }) => {
        const values = [...new Set(available.map((variant) => variant.options[index]))];
        return `<label class="evira-upsell__select"><span>${this.escape(name)}</span><select data-evira-upsell-option aria-label="${this.escape(name)} for ${this.escape(product.title)}">${values.map((value) =>
          `<option value="${this.escape(value)}" ${value === first.options[index] ? 'selected' : ''}>${this.escape(value)}</option>`).join('')}</select></label>`;
      }).join('');
      const rawImage = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.src || product.images?.[0];
      const image = typeof rawImage === 'string' && /^(https?:)?\/\//.test(rawImage) ?
        `<img src="${this.escape(rawImage)}" alt="" loading="lazy" width="84" height="105">` : '';
      return `<article class="evira-upsell__card" data-evira-upsell-product="${Number(product.id)}">
        <a href="${this.escape(product.url || `/products/${product.handle}`)}" class="evira-upsell__image">${image}</a>
        <div class="evira-upsell__details"><a href="${this.escape(product.url || `/products/${product.handle}`)}" class="evira-upsell__title">${this.escape(product.title)}</a>
          <strong data-evira-upsell-price>${this.money(first.price)}</strong>
          <div class="evira-upsell__options">${options}</div>
          <button type="button" class="evira-upsell__add" data-evira-upsell-add data-variant-id="${Number(first.id)}">Add to bag +</button>
        </div></article>`;
    }
  }

  document.addEventListener('DOMContentLoaded', () => new EviraCartEnhancements());
})();
